import {
    Box,
    Link,
    LinkProps,
    Stack,
    SxProps,
    Typography,
} from "@mui/material";
import React from "react";
import theme from "Theme/AppTheme";
import LoginScreenBackground from "../../loginScreenBackground.svg";
import AcxLogoLoader from "components/UI/Loaders/AcxLogoLoader";

type Props = {
    title?: string;
    subtitle?: string;
    subTitleSx?: SxProps;
    children?: React.ReactNode;
    isLoading?: boolean;
    shouldShowLogo?: boolean;
    shouldShowLinks?: boolean;
    header?: React.ReactNode;
};

const AuthLink = ({ children, ...props }: LinkProps) => (
    <Link
        {...props}
        target="_blank"
        sx={{
            color: theme.palette.gray[400],
            textDecoration: "underline",
            p: 1,
            lineHeight: "20px",
            fontSize: "14px",
        }}
    >
        {children}
    </Link>
);

export default function AuthPage({
    title,
    subtitle,
    subTitleSx = {},
    children,
    shouldShowLogo = true,
    isLoading = false,
    header,
    shouldShowLinks = false,
}: Props) {
    return (
        <Box
            sx={{
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundColor: "#213FA4",
                backgroundImage: `url(${LoginScreenBackground})`,
                padding: 0,
                minHeight: "100vh",
                height: "100%",
                width: "100vw",
            }}
        >
            <Stack
                sx={{
                    backgroundColor: theme.palette.white.main,
                    minHeight: "100vh",
                    height: "100vh",
                    width: {
                        xs: "100%",
                        sm: "55.4%",
                    },
                    maxWidth: "850px",
                    py: 5,
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Stack
                    sx={{
                        flex: "1 1 auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "450px",
                        maxWidth: "calc(100% - 48px)",
                    }}
                >
                    <Stack
                        sx={{
                            position: "relative",
                            width: "100%",
                        }}
                    >
                        {header && header}

                        {shouldShowLogo && (
                            <AcxLogoLoader
                                height={55}
                                width={55}
                                isSpinning={isLoading}
                            />
                        )}
                        {(title || subtitle) && (
                            <Box sx={{ my: 5 }}>
                                {title && (
                                    <Typography
                                        sx={{
                                            color: theme.palette.gray[600],
                                            fontSize: 24,
                                            fontWeight: 700,
                                            lineHeight: "40px",
                                            mb: 0.5,
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                )}
                                {subtitle && (
                                    <Typography
                                        sx={{
                                            color: theme.palette.gray[500],
                                            fontSize: 16,
                                            fontWeight: "normal",
                                            lineHeight: "24px",
                                            ...subTitleSx,
                                        }}
                                    >
                                        {subtitle}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {children}
                    </Stack>
                </Stack>
                {shouldShowLinks && (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            width: "100%",
                            justifyContent: "center",
                            gap: 2,
                        }}
                    >
                        <AuthLink href="https://www.authenticxhelp.com/en/">
                            Help
                        </AuthLink>
                        <AuthLink href="https://authenticx.com/terms-conditions/">
                            Terms
                        </AuthLink>
                        <AuthLink href="https://authenticx.com/privacy-policy/">
                            Privacy
                        </AuthLink>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}
